import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import CallMergeIcon from '@mui/icons-material/CallMerge';

interface BusinessMergeDialogProps {
  open: boolean;
  onClose: () => void;
  onMerged: () => void;
  initialBusiness: { id: string; name: string; abn: string } | null;
}

interface BusinessOption {
  id: string;
  name: string;
  abn: string;
  deviceCount: number;
  isArchived: boolean;
}

interface AddressValue {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  postCode?: string | null;
  country?: string | null;
}

interface MergeFieldValues {
  name: string;
  abn: string;
  address: AddressValue | null;
  primaryUserId: string;
  primaryUserName: string;
}

interface MergeCandidate {
  id: string;
  fields: MergeFieldValues;
  dateCreated: string;
  deviceCount: number;
  contactCount: number;
  userCount: number;
  activeSubscriptionCount: number;
  spatialLayerCount: number;
  recordCount: number;
  invoiceCount: number;
  apiKeyCount: number;
  pendingJoinRequestCount: number;
  hasActiveParentAssociation: boolean;
  activeChildAssociationCount: number;
  hasDefaultPaymentProfile: boolean;
  farmAliasCount: number;
  fieldAliasCount: number;
  businessAliasCount: number;
  importBatchCount: number;
}

type ConflictType =
  | 'ActiveSubscriptionOverlap'
  | 'DefaultPaymentProfile'
  | 'PendingJoinRequestOverlap'
  | 'ActiveParentAssociation'
  | 'DirectAssociationBetweenTargets';

interface MergeConflict {
  type: ConflictType;
  blocking: boolean;
  description: string;
  planFamily?: string;
  loserSubscriptionId?: string;
  winnerSubscriptionId?: string;
}

interface MergePreview {
  loser: MergeCandidate;
  winner: MergeCandidate;
  conflicts: MergeConflict[];
  canProceed: boolean;
}

type FieldSource = 'Winner' | 'Loser';

interface FieldChoices {
  nameSource: FieldSource;
  abnSource: FieldSource;
  addressSource: FieldSource;
  primaryUserSource: FieldSource;
}

const steps = ['Pick business to compare', 'Compare & resolve', 'Confirm'];

const formatAddress = (a: AddressValue | null) => {
  if (!a) return '—';
  const parts = [a.street, a.city, a.state, a.postCode, a.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
};

const BusinessMergeDialog: React.FC<BusinessMergeDialogProps> = ({ open, onClose, onMerged, initialBusiness }) => {
  const auth = useAuth();
  const authHeader = { Authorization: `Bearer ${auth.tokens?.accessToken}` };

  const [step, setStep] = useState(0);

  const [businessOptions, setBusinessOptions] = useState<BusinessOption[]>([]);
  const [otherBusiness, setOtherBusiness] = useState<BusinessOption | null>(null);
  const [initialIsWinner, setInitialIsWinner] = useState(true);

  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [fieldChoices, setFieldChoices] = useState<FieldChoices>({
    nameSource: 'Winner',
    abnSource: 'Winner',
    addressSource: 'Winner',
    primaryUserSource: 'Winner',
  });
  const [subscriptionResolutions, setSubscriptionResolutions] = useState<Record<string, string>>({});
  const [paymentProfileResolution, setPaymentProfileResolution] = useState<FieldSource | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !initialBusiness) return;
    fetch('/api/businesses/list', { headers: authHeader })
      .then((r) => (r.ok ? r.json() : []))
      .then((data: BusinessOption[]) => setBusinessOptions(data.filter((b) => b.id !== initialBusiness.id && !b.isArchived)))
      .catch(() => setBusinessOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialBusiness]);

  const resetAll = () => {
    setStep(0);
    setOtherBusiness(null);
    setInitialIsWinner(true);
    setPreview(null);
    setPreviewError(null);
    setFieldChoices({ nameSource: 'Winner', abnSource: 'Winner', addressSource: 'Winner', primaryUserSource: 'Winner' });
    setSubscriptionResolutions({});
    setPaymentProfileResolution(null);
    setSubmitError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    resetAll();
    onClose();
  };

  const winnerId = !initialBusiness ? null : initialIsWinner ? initialBusiness.id : otherBusiness?.id ?? null;
  const loserId = !initialBusiness ? null : initialIsWinner ? otherBusiness?.id ?? null : initialBusiness.id;

  const loadPreview = async () => {
    if (!winnerId || !loserId) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const response = await fetch(`/api/admin/business-merge/preview?loserId=${loserId}&winnerId=${winnerId}`, {
        headers: authHeader,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load merge preview');
      }
      setPreview(data);
      // Pre-fill subscription resolutions with the winner's subscription by default.
      const defaults: Record<string, string> = {};
      (data.conflicts as MergeConflict[]).forEach((c) => {
        if (c.type === 'ActiveSubscriptionOverlap' && c.planFamily && c.winnerSubscriptionId) {
          defaults[c.planFamily] = c.winnerSubscriptionId;
        }
      });
      setSubscriptionResolutions(defaults);
      if ((data.conflicts as MergeConflict[]).some((c) => c.type === 'DefaultPaymentProfile')) {
        setPaymentProfileResolution('Winner');
      }
      setStep(1);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Failed to load merge preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const hasUnresolvedBlocking = useMemo(() => preview?.conflicts.some((c) => c.blocking) ?? false, [preview]);

  const handleExecute = async () => {
    if (!winnerId || !loserId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const response = await fetch('/api/admin/business-merge/execute', {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          loserBusinessId: loserId,
          winnerBusinessId: winnerId,
          fieldChoices,
          subscriptionResolutions: Object.entries(subscriptionResolutions).map(([planFamily, keepSubscriptionId]) => ({
            planFamily,
            keepSubscriptionId,
          })),
          paymentProfileResolution,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to merge businesses');
      }
      onMerged();
      handleClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to merge businesses');
    } finally {
      setSubmitting(false);
    }
  };

  if (!initialBusiness) return null;

  const winnerName = initialIsWinner ? initialBusiness.name : otherBusiness?.name ?? '';
  const loserName = initialIsWinner ? otherBusiness?.name ?? '' : initialBusiness.name;

  const renderFieldRow = (
    label: string,
    key: keyof FieldChoices,
    loserValue: string,
    winnerValue: string,
  ) => (
    <TableRow>
      <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
      <TableCell>{loserValue || '—'}</TableCell>
      <TableCell>{winnerValue || '—'}</TableCell>
      <TableCell>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={fieldChoices[key]}
          onChange={(_, val) => {
            if (val) setFieldChoices({ ...fieldChoices, [key]: val });
          }}
        >
          <ToggleButton value="Loser">Keep loser's</ToggleButton>
          <ToggleButton value="Winner">Keep winner's</ToggleButton>
        </ToggleButtonGroup>
      </TableCell>
    </TableRow>
  );

  const renderCountsRow = (label: string, loserValue: number | boolean, winnerValue: number | boolean) => (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>{typeof loserValue === 'boolean' ? (loserValue ? 'Yes' : 'No') : loserValue}</TableCell>
      <TableCell>{typeof winnerValue === 'boolean' ? (winnerValue ? 'Yes' : 'No') : winnerValue}</TableCell>
    </TableRow>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CallMergeIcon />
          Merge Businesses
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
          {steps.map((s) => (
            <Step key={s}>
              <StepLabel>{s}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Merging archives one business and moves all of its data (devices, contacts, records, subscriptions,
              and everything else) onto the other. This cannot be easily undone.
            </Typography>
            <Alert severity="info">
              <strong>{initialBusiness.name}</strong> (ABN {initialBusiness.abn || '—'})
            </Alert>
            <Autocomplete
              options={businessOptions}
              getOptionLabel={(o) => `${o.name} (ABN ${o.abn || '—'})`}
              value={otherBusiness}
              onChange={(_, val) => setOtherBusiness(val)}
              renderInput={(params) => <TextField {...params} label="Business to merge with" />}
            />
            {otherBusiness && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Which business should survive?
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={initialIsWinner ? 'initial' : 'other'}
                  onChange={(_, val) => {
                    if (val) setInitialIsWinner(val === 'initial');
                  }}
                >
                  <ToggleButton value="initial">{initialBusiness.name} survives</ToggleButton>
                  <ToggleButton value="other">{otherBusiness.name} survives</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
            {previewError && <Alert severity="error">{previewError}</Alert>}
          </Box>
        )}

        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {previewLoading || !preview ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {preview.conflicts.filter((c) => c.blocking).map((c, i) => (
                  <Alert severity="error" key={`blocking-${i}`}>
                    {c.description}
                  </Alert>
                ))}

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Field values — loser: {loserName}, winner: {winnerName}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          <TableCell>Loser's value</TableCell>
                          <TableCell>Winner's value</TableCell>
                          <TableCell>Keep</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {renderFieldRow('Name', 'nameSource', preview.loser.fields.name, preview.winner.fields.name)}
                        {renderFieldRow('ABN', 'abnSource', preview.loser.fields.abn, preview.winner.fields.abn)}
                        {renderFieldRow(
                          'Address',
                          'addressSource',
                          formatAddress(preview.loser.fields.address),
                          formatAddress(preview.winner.fields.address),
                        )}
                        {renderFieldRow(
                          'Primary user',
                          'primaryUserSource',
                          preview.loser.fields.primaryUserName,
                          preview.winner.fields.primaryUserName,
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {preview.conflicts.filter((c) => c.type === 'ActiveSubscriptionOverlap').map((c) => (
                  <Alert severity="warning" key={`sub-${c.planFamily}`}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {c.description}
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={subscriptionResolutions[c.planFamily!] ?? ''}
                      onChange={(_, val) => {
                        if (val) setSubscriptionResolutions({ ...subscriptionResolutions, [c.planFamily!]: val });
                      }}
                    >
                      <ToggleButton value={c.loserSubscriptionId!}>Keep loser's</ToggleButton>
                      <ToggleButton value={c.winnerSubscriptionId!}>Keep winner's</ToggleButton>
                    </ToggleButtonGroup>
                  </Alert>
                ))}

                {preview.conflicts.some((c) => c.type === 'DefaultPaymentProfile') && (
                  <Alert severity="warning">
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Both businesses have a default payment profile on file. Pick which one stays the default.
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={paymentProfileResolution ?? ''}
                      onChange={(_, val) => {
                        if (val) setPaymentProfileResolution(val);
                      }}
                    >
                      <ToggleButton value="Loser">Keep loser's</ToggleButton>
                      <ToggleButton value="Winner">Keep winner's</ToggleButton>
                    </ToggleButtonGroup>
                  </Alert>
                )}

                <Divider />

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Data that will move to the winner
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Where the same contact/device/user/farm-alias/field-alias link already exists on both sides, the
                    winner's existing link is kept and the loser's duplicate is dropped.
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Data</TableCell>
                          <TableCell>Loser ({loserName})</TableCell>
                          <TableCell>Winner ({winnerName})</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {renderCountsRow('Devices', preview.loser.deviceCount, preview.winner.deviceCount)}
                        {renderCountsRow('Contacts', preview.loser.contactCount, preview.winner.contactCount)}
                        {renderCountsRow('Users', preview.loser.userCount, preview.winner.userCount)}
                        {renderCountsRow('Records', preview.loser.recordCount, preview.winner.recordCount)}
                        {renderCountsRow('Spatial layers (farms/fields)', preview.loser.spatialLayerCount, preview.winner.spatialLayerCount)}
                        {renderCountsRow('Active subscriptions', preview.loser.activeSubscriptionCount, preview.winner.activeSubscriptionCount)}
                        {renderCountsRow('Invoices', preview.loser.invoiceCount, preview.winner.invoiceCount)}
                        {renderCountsRow('API keys', preview.loser.apiKeyCount, preview.winner.apiKeyCount)}
                        {renderCountsRow('Import batches', preview.loser.importBatchCount, preview.winner.importBatchCount)}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            )}
          </Box>
        )}

        {step === 2 && preview && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Alert severity="warning">
              This cannot be easily undone. <strong>{loserName}</strong> will be archived and all of its data moved
              onto <strong>{winnerName}</strong>.
            </Alert>
            <Typography variant="body2">Name: {fieldChoices.nameSource === 'Loser' ? loserName : winnerName}'s value</Typography>
            <Typography variant="body2">ABN: {fieldChoices.abnSource === 'Loser' ? loserName : winnerName}'s value</Typography>
            <Typography variant="body2">Address: {fieldChoices.addressSource === 'Loser' ? loserName : winnerName}'s value</Typography>
            <Typography variant="body2">
              Primary user: {fieldChoices.primaryUserSource === 'Loser' ? loserName : winnerName}'s value
            </Typography>
            {Object.entries(subscriptionResolutions).map(([family, keepId]) => (
              <Typography variant="body2" key={family}>
                {family} subscription: keeping {keepId === preview.winner.id ? winnerName : loserName}'s
              </Typography>
            ))}
            {paymentProfileResolution && (
              <Typography variant="body2">
                Default payment profile: keeping {paymentProfileResolution === 'Loser' ? loserName : winnerName}'s
              </Typography>
            )}
            {submitError && <Alert severity="error">{submitError}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        {step > 0 && (
          <Button onClick={() => setStep(step - 1)} disabled={submitting || previewLoading}>
            Back
          </Button>
        )}
        {step === 0 && (
          <Button variant="contained" disabled={!otherBusiness || previewLoading} onClick={loadPreview}>
            {previewLoading ? <CircularProgress size={20} /> : 'Next'}
          </Button>
        )}
        {step === 1 && (
          <Button
            variant="contained"
            disabled={previewLoading || !preview || hasUnresolvedBlocking}
            onClick={() => setStep(2)}
          >
            Next
          </Button>
        )}
        {step === 2 && (
          <Button variant="contained" color="warning" disabled={submitting} onClick={handleExecute}>
            {submitting ? <CircularProgress size={20} /> : 'Confirm Merge'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BusinessMergeDialog;
